import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { SakuraFlower } from "./SakuraDecorations";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [expandedMobileCategories, setExpandedMobileCategories] = useState({});
  const [hoveredTreatment, setHoveredTreatment] = useState(null);
  const location = useLocation();
  const dropdownRef = useRef(null);
  const dropdownTimeoutRef = useRef(null);

  const handleDropdownEnter = () => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setServicesDropdownOpen(true);
  };

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setServicesDropdownOpen(false);
    }, 150);
  };

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
    setMobileServicesOpen(false);
    setExpandedMobileCategories({});
  }, [location]);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle click outside dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setServicesDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const serviceLinks = [
    {
      title: "Akupunktur",
      href: "/akupunktur",
      desc: "Klassisk og traditionel behandling",
      categories: [
        { title: "Stress", href: "/stress" },
        { title: "Muskelspændinger", href: "/spaendinger" },
        { title: "Rygsmerter", href: "/ondt-i-ryggen" },
        { title: "Led og muskelsmerter", href: "/led-og-muskelsmerter" },
      ],
    },
    {
      title: "Japansk akupunktur",
      href: "/japansk-akupunktur",
      desc: "Nænsom Keiraku Chiryo meridianterapi",
      categories: [
        { title: "Migræne", href: "/migraene" },
        { title: "Kæbesmerter & spændinger", href: "/ondt-i-kaeben" },
      ],
    },
    { title: "Øre akupunktur", href: "/oere-akupunktur", desc: "Målrettet ørezone stimulering" },
    { title: "Vægtregulering", href: "/vaegtregulering", desc: "Støtte til sund vægt og balance" },
    { title: "Rygestop behandling", href: "/rygestop-behandling", desc: "Effektiv hjælp til rygeophør" },
  ];

  const isServicesActive =
    serviceLinks.some(
      (item) =>
        location.pathname === item.href ||
        location.pathname === `${item.href}/` ||
        (item.categories &&
          item.categories.some(
            (cat) =>
              location.pathname === cat.href ||
              location.pathname === `${cat.href}/`
          ))
    ) ||
    location.pathname.startsWith("/stress") ||
    location.pathname.startsWith("/led-og-muskel") ||
    location.pathname.startsWith("/muskelspaendinger") ||
    location.pathname.startsWith("/spaendinger") ||
    location.pathname.startsWith("/ondt-i-ryggen") ||
    location.pathname.startsWith("/migraene") ||
    location.pathname.startsWith("/ondt-i-kaeben") ||
    location.pathname.startsWith("/kaebesmerter");

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-xl shadow-xs border-b border-[#ffe0cf] py-3"
            : "bg-[#fffaf7]/95 backdrop-blur-md border-b border-[#ffe0cf]/80 py-3.5"
        }`}
      >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center space-x-3 group focus:outline-none focus:ring-2 focus:ring-[#d74814]/30 rounded-lg p-0.5"
            aria-label="Kita Akupunktur Forside"
          >
            <div className="relative">
              <img
                src="https://assets.auxodev.dk/assets/71339d65-a05d-4c02-a540-2260acdfc245/kita-akupunktur-logo.png"
                alt="Kita Akupunktur logo"
                className="h-11 sm:h-12 w-auto object-contain transition-transform duration-200 group-hover:scale-[1.02]"
                width="200"
                height="50"
              />
            </div>
          </Link>

          {/* Desktop Navigation (>= 1200px only) */}
          <nav
            className="hidden min-[1200px]:flex items-center space-x-8"
            aria-label="Hovednavigation"
          >
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `text-sm font-medium transition-colors pb-1 border-b-2 ${
                  isActive
                    ? "text-[#d74814] border-[#d74814] font-semibold"
                    : "text-[#593427] border-transparent hover:text-[#d74814] hover:border-[#ffe0cf]"
                }`
              }
            >
              Forside
            </NavLink>

            <NavLink
              to="/om-mig"
              className={({ isActive }) =>
                `text-sm font-medium transition-colors pb-1 border-b-2 ${
                  isActive
                    ? "text-[#d74814] border-[#d74814] font-semibold"
                    : "text-[#593427] border-transparent hover:text-[#d74814] hover:border-[#ffe0cf]"
                }`
              }
            >
              Om mig
            </NavLink>

            {/* Dropdown Menu for Services */}
            <div
              className="relative"
              ref={dropdownRef}
              onMouseEnter={handleDropdownEnter}
              onMouseLeave={handleDropdownLeave}
            >
              <button
                type="button"
                onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
                className={`text-sm font-medium transition-colors pb-1 border-b-2 flex items-center space-x-1.5 focus:outline-none ${
                  isServicesActive || servicesDropdownOpen
                    ? "text-[#d74814] border-[#d74814] font-semibold"
                    : "text-[#593427] border-transparent hover:text-[#d74814] hover:border-[#ffe0cf]"
                }`}
                aria-expanded={servicesDropdownOpen}
              >
                <span>Behandlinger</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                    servicesDropdownOpen ? "rotate-180 text-[#d74814]" : ""
                  }`}
                  aria-hidden="true"
                />
              </button>

              {servicesDropdownOpen && (
                <div className="absolute left-0 mt-2 w-84 bg-white/95 backdrop-blur-2xl rounded-2xl shadow-[0_16px_40px_rgba(89,52,39,0.12)] border border-[#ffe0cf] p-2 z-50 transition-all duration-200 animate-in fade-in slide-in-from-top-1">
                  <div className="flex items-center justify-between px-3 py-1.5 mb-1 border-b border-[#ffe0cf]/60 text-[11px] font-semibold uppercase tracking-wider text-[#d74814]">
                    <span>Behandlinger & Ydelser</span>
                    <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                  </div>
                  <div className="space-y-1">
                    {serviceLinks.map((item) => {
                      const isSelfActive =
                        location.pathname === item.href || location.pathname === `${item.href}/`;
                      const isAnyChildActive =
                        item.categories &&
                        item.categories.some(
                          (cat) =>
                            location.pathname === cat.href ||
                            location.pathname === `${cat.href}/`
                        );
                      const active = isSelfActive || isAnyChildActive;
                      const isExpanded = hoveredTreatment === item.href;

                      return (
                        <div
                          key={item.href}
                          className="group/item"
                          onMouseEnter={() => setHoveredTreatment(item.href)}
                          onMouseLeave={() => setHoveredTreatment(null)}
                        >
                          <Link
                            to={item.href}
                            onClick={() => setServicesDropdownOpen(false)}
                            className={`flex items-center space-x-3 px-3 py-2 transition-all rounded-xl ${
                              active
                                ? "bg-[#ffe0cf]/60 text-[#d74814] font-medium"
                                : "text-[#593427] hover:bg-[#fff5ee] hover:text-[#d74814]"
                            }`}
                          >
                            <div className="w-7 h-7 rounded-lg bg-[#ffe0cf]/50 flex items-center justify-center text-[#d74814] shrink-0 border border-[#ffe0cf]">
                              <SakuraFlower className="w-3.5 h-3.5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium leading-tight flex items-center justify-between">
                                <span>{item.title}</span>
                                {item.categories && item.categories.length > 0 && (
                                  <i
                                    className={`fa-solid fa-chevron-right text-[10px] text-[#7a5b4f]/40 transition-all duration-200 ${
                                      isExpanded
                                        ? "text-[#d74814] rotate-90"
                                        : "group-hover/item:text-[#d74814]"
                                    }`}
                                    aria-hidden="true"
                                  />
                                )}
                              </div>
                              <div className="text-[11px] text-stone-500 line-clamp-1">{item.desc}</div>
                            </div>
                          </Link>

                          {item.categories && item.categories.length > 0 && (
                            <div
                              className={`transition-all duration-200 ease-out overflow-hidden ${
                                isExpanded
                                  ? "max-h-60 opacity-100 pointer-events-auto"
                                  : "max-h-0 opacity-0 pointer-events-none"
                              }`}
                            >
                              <div className="ml-10 my-1 pl-3 border-l-2 border-[#ffe0cf] space-y-0.5 py-1">
                                {item.categories.map((cat) => {
                                  const isCatActive =
                                    location.pathname === cat.href ||
                                    location.pathname === `${cat.href}/`;
                                  return (
                                    <Link
                                      key={cat.href}
                                      to={cat.href}
                                      onClick={() => {
                                        setServicesDropdownOpen(false);
                                        setHoveredTreatment(null);
                                      }}
                                      className={`flex items-center space-x-2 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                                        isCatActive
                                          ? "bg-[#ffe0cf]/80 text-[#d74814] font-semibold"
                                          : "text-[#7a5b4f] hover:text-[#d74814] hover:bg-[#fff5ee]"
                                      }`}
                                    >
                                      <span className="w-1.5 h-1.5 rounded-full bg-[#d74814]/70 shrink-0" />
                                      <span>{cat.title}</span>
                                    </Link>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <NavLink
              to="/priser"
              className={({ isActive }) =>
                `text-sm font-medium transition-colors pb-1 border-b-2 ${
                  isActive
                    ? "text-[#d74814] border-[#d74814] font-semibold"
                    : "text-[#593427] border-transparent hover:text-[#d74814] hover:border-[#ffe0cf]"
                }`
              }
            >
              Priser
            </NavLink>

            <NavLink
              to="/faq"
              className={({ isActive }) =>
                `text-sm font-medium transition-colors pb-1 border-b-2 ${
                  isActive
                    ? "text-[#d74814] border-[#d74814] font-semibold"
                    : "text-[#593427] border-transparent hover:text-[#d74814] hover:border-[#ffe0cf]"
                }`
              }
            >
              FAQ
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `text-sm font-medium transition-colors pb-1 border-b-2 ${
                  isActive
                    ? "text-[#d74814] border-[#d74814] font-semibold"
                    : "text-[#593427] border-transparent hover:text-[#d74814] hover:border-[#ffe0cf]"
                }`
              }
            >
              Kontakt
            </NavLink>
          </nav>

          {/* Desktop Right CTA */}
          <div className="hidden min-[1200px]:flex items-center space-x-3">
            <a
              href="tel:29387186"
              className="inline-flex items-center text-xs text-[#593427] hover:text-[#d74814] px-3 py-1.5 rounded-full bg-[#ffe0cf]/40 border border-[#ffe0cf] transition-colors"
            >
              <i className="fa-solid fa-phone mr-1.5 text-[#d74814]" aria-hidden="true" />
              <span className="font-semibold">29 38 71 86</span>
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-medium bg-[#d74814] text-white hover:bg-[#b83a0d] transition-all shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#d74814] group"
            >
              <SakuraFlower className="w-3.5 h-3.5 mr-2 opacity-90 transition-transform group-hover:rotate-45" />
              <span>Bestil tid / Kontakt</span>
            </Link>
          </div>

          {/* Mobile Actions: Call Button + Hamburger */}
          <div className="flex items-center space-x-2 min-[1200px]:hidden">
            <a
              href="tel:29387186"
              className="p-2.5 rounded-full bg-[#d74814] text-white text-xs hover:bg-[#b83a0d] transition-colors inline-flex items-center justify-center shadow-xs"
              aria-label="Ring op på 29 38 71 86"
            >
              <i className="fa-solid fa-phone text-sm" aria-hidden="true" />
            </a>

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2.5 rounded-xl bg-white/90 backdrop-blur-md border border-[#ffe0cf] text-[#593427] hover:text-[#d74814] hover:bg-[#fff5ee] transition-colors focus:outline-none focus:ring-2 focus:ring-[#d74814]"
              aria-label={mobileMenuOpen ? "Luk menu" : "Åbn menu"}
              aria-expanded={mobileMenuOpen}
            >
              <i
                className={`fa-solid ${mobileMenuOpen ? "fa-xmark" : "fa-bars"} text-lg`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-white/95 backdrop-blur-xl border-b border-[#ffe0cf] px-4 pt-3 pb-6 space-y-3 animate-in slide-in-from-top duration-200">
          <div className="space-y-1">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `flex items-center px-4 py-2.5 rounded-xl text-base font-medium transition-colors ${
                  isActive
                    ? "bg-[#d74814] text-white"
                    : "text-[#593427] hover:bg-[#fff5ee] hover:text-[#d74814]"
                }`
              }
            >
              Forside
            </NavLink>

            <NavLink
              to="/om-mig"
              className={({ isActive }) =>
                `flex items-center px-4 py-2.5 rounded-xl text-base font-medium transition-colors ${
                  isActive
                    ? "bg-[#d74814] text-white"
                    : "text-[#593427] hover:bg-[#fff5ee] hover:text-[#d74814]"
                }`
              }
            >
              Om mig
            </NavLink>

            {/* Mobile Services Accordion */}
            <div>
              <button
                type="button"
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-base font-medium transition-colors ${
                  isServicesActive
                    ? "bg-[#ffe0cf]/50 text-[#d74814] font-semibold"
                    : "text-[#593427] hover:bg-[#fff5ee] hover:text-[#d74814]"
                }`}
              >
                <span>Behandlinger</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform ${
                    mobileServicesOpen ? "rotate-180" : ""
                  }`}
                  aria-hidden="true"
                />
              </button>

              {mobileServicesOpen && (
                <div className="pl-4 pr-2 py-2 space-y-2 bg-[#fffaf7] rounded-xl my-1 border border-[#ffe0cf]">
                  {serviceLinks.map((item) => {
                    const active =
                      location.pathname === item.href || location.pathname === `${item.href}/`;
                    const hasCategories = item.categories && item.categories.length > 0;
                    const isExpanded = !!expandedMobileCategories[item.href];

                    return (
                      <div key={item.href} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <Link
                            to={item.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`flex-1 flex items-center space-x-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                              active
                                ? "bg-[#ffe0cf]/70 text-[#d74814] font-semibold"
                                : "text-[#593427] hover:text-[#d74814] hover:bg-[#fff5ee]"
                            }`}
                          >
                            <SakuraFlower className="w-3 h-3 text-[#d74814]" />
                            <span>{item.title}</span>
                          </Link>
                          {hasCategories && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedMobileCategories((prev) => ({
                                  ...prev,
                                  [item.href]: !prev[item.href],
                                }));
                              }}
                              className="p-2 text-[#7a5b4f] hover:text-[#d74814] focus:outline-none"
                              aria-label={`Vis underkategorier for ${item.title}`}
                            >
                              <i
                                className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                                  isExpanded ? "rotate-180 text-[#d74814]" : ""
                                }`}
                                aria-hidden="true"
                              />
                            </button>
                          )}
                        </div>
                        {hasCategories && isExpanded && (
                          <div className="ml-6 pl-3 border-l-2 border-[#ffe0cf] space-y-1 py-1 animate-in fade-in duration-200">
                            {item.categories.map((cat) => {
                              const isCatActive =
                                location.pathname === cat.href ||
                                location.pathname === `${cat.href}/`;
                              return (
                                <Link
                                  key={cat.href}
                                  to={cat.href}
                                  onClick={() => setMobileMenuOpen(false)}
                                  className={`flex items-center space-x-2 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                    isCatActive
                                      ? "bg-[#ffe0cf]/80 text-[#d74814] font-semibold"
                                      : "text-[#7a5b4f] hover:text-[#d74814] hover:bg-[#fff5ee]"
                                  }`}
                                >
                                  <span className="w-1.5 h-1.5 rounded-full bg-[#d74814]/70 shrink-0" />
                                  <span>{cat.title}</span>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <NavLink
              to="/priser"
              className={({ isActive }) =>
                `flex items-center px-4 py-2.5 rounded-xl text-base font-medium transition-colors ${
                  isActive
                    ? "bg-[#d74814] text-white"
                    : "text-[#593427] hover:bg-[#fff5ee] hover:text-[#d74814]"
                }`
              }
            >
              Priser
            </NavLink>

            <NavLink
              to="/faq"
              className={({ isActive }) =>
                `flex items-center px-4 py-2.5 rounded-xl text-base font-medium transition-colors ${
                  isActive
                    ? "bg-[#d74814] text-white"
                    : "text-[#593427] hover:bg-[#fff5ee] hover:text-[#d74814]"
                }`
              }
            >
              FAQ
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `flex items-center px-4 py-2.5 rounded-xl text-base font-medium transition-colors ${
                  isActive
                    ? "bg-[#d74814] text-white"
                    : "text-[#593427] hover:bg-[#fff5ee] hover:text-[#d74814]"
                }`
              }
            >
              Kontakt
            </NavLink>
          </div>

          <div className="pt-3 border-t border-[#ffe0cf] space-y-2">
            <Link
              to="/kontakt"
              className="w-full inline-flex items-center justify-center px-4 py-3 rounded-full bg-[#d74814] text-white font-medium text-sm hover:bg-[#b83a0d] transition-colors shadow-sm"
            >
              <SakuraFlower className="w-4 h-4 mr-2" />
              <span>Bestil tid / Kontakt</span>
            </Link>

            <div className="flex items-center justify-center space-x-4 pt-1 text-xs text-[#593427]">
              <a
                href="tel:29387186"
                className="flex items-center hover:text-[#d74814] transition-colors"
              >
                <i className="fa-solid fa-phone mr-1.5 text-[#d74814]" aria-hidden="true" />
                29 38 71 86
              </a>
              <span>•</span>
              <a
                href="mailto:kontakt@kita-akupunktur.dk"
                className="flex items-center hover:text-[#d74814] transition-colors"
              >
                <i className="fa-solid fa-envelope mr-1.5 text-[#d74814]" aria-hidden="true" />
                kontakt@kita-akupunktur.dk
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
    {/* Spacer to prevent content jump behind fixed header */}
    <div className="h-[68px] sm:h-[72px] w-full shrink-0" aria-hidden="true" />
  </>
);
}
